import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ArrowsClockwise, CheckCircle, Notepad, Globe, Sparkle,
  ArrowRight, Check, MagnifyingGlass,
} from "@phosphor-icons/react";

import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api } from "../lib/kostra";

/* -------------------------------------------------------------------------
 * Tab 1 — Sync from My Recipes
 * Pulls newest-used rate per ingredient from saved recipes, surfaces
 * mismatches against master, lets user apply selected suggestions in bulk.
 * ----------------------------------------------------------------------- */
export function RecipeSyncPanel({ onApplied }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ suggestions: [], recipe_count: 0 });
  const [selected, setSelected] = useState(() => new Set());
  const [applying, setApplying] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true); setSelected(new Set());
    try {
      const { data } = await api.get("/ingredients/master/sync-recipes");
      setData(data);
    } catch { toast.error("Sync scan failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return data.suggestions || [];
    return (data.suggestions || []).filter((s) =>
      (s.name || "").toLowerCase().includes(needle));
  }, [data.suggestions, q]);

  const toggle = (name) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(name)) n.delete(name); else n.add(name);
      return n;
    });
  };
  const selectAll = () => setSelected(new Set(filtered.map((s) => s.name)));
  const clearAll = () => setSelected(new Set());

  const applyBatch = async () => {
    const items = (data.suggestions || [])
      .filter((s) => selected.has(s.name))
      .map((s) => ({ name: s.name, rate_per_kg: s.suggested_rate }));
    if (!items.length) { toast.info("Nothing selected"); return; }
    setApplying(true);
    try {
      const res = await api.post("/ingredients/master/apply-batch", { items });
      toast.success(`Applied ${res.data.applied.length} updates`);
      await load();
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch apply failed");
    } finally { setApplying(false); }
  };

  return (
    <div className="space-y-5">
      <div className="kostra-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="kostra-section-title text-lg flex items-center gap-2">
              <Notepad size={16} weight="duotone" className="text-[#D4AF37]" />
              Sync from My Recipes
            </h2>
            <p className="text-xs text-[#4B5563] mt-1 max-w-2xl">
              Scans your {data.recipe_count} saved recipes and suggests the
              <strong className="text-[#0A3B24]"> most-recently-used rate </strong>
              per ingredient. Only ingredients whose recipe rate differs from
              the current master are listed.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}
                  className="border-[#0A3B24]/30 text-[#0A3B24] hover:bg-[#FDF9EE] font-semibold"
                  data-testid="sync-recipes-refresh">
            <ArrowsClockwise size={14} weight="bold" className="mr-1.5" />
            Re-scan recipes
          </Button>
        </div>
      </div>

      <div className="kostra-card p-0 overflow-hidden" data-testid="sync-recipes-panel">
        <div className="px-5 py-3 flex items-center justify-between gap-3 border-b border-[#E5E7EB] flex-wrap">
          <div className="relative">
            <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5563]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Filter ingredients…"
                   className="h-9 pl-8 w-64 text-sm" data-testid="sync-recipes-search" />
          </div>
          <div className="text-xs text-[#4B5563]">
            {filtered.length} mismatch{filtered.length === 1 ? "" : "es"} ·
            <span className="text-[#0A3B24] font-semibold ml-1">{selected.size} selected</span>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={selectAll}
                    className="text-xs font-semibold text-[#0A3B24] hover:underline">Select all</button>
            <span className="text-[#E5E7EB] text-xs">·</span>
            <button type="button" onClick={clearAll}
                    className="text-xs font-semibold text-[#4B5563] hover:underline">Clear</button>
            <Button onClick={applyBatch} disabled={!selected.size || applying}
                    className="h-9 bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold disabled:opacity-50"
                    data-testid="sync-recipes-apply">
              <CheckCircle size={13} weight="fill" className="mr-1.5" />
              {applying ? "Applying…" : `Apply ${selected.size || ""}`}
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="sync-recipes-table">
            <thead className="bg-[#F9FAFB] text-[10px] uppercase tracking-wider text-[#4B5563]">
              <tr>
                <th className="text-center px-3 py-2.5 font-bold w-10"></th>
                <th className="text-left px-4 py-2.5 font-bold">Ingredient</th>
                <th className="text-right px-3 py-2.5 font-bold">Master Rate</th>
                <th className="text-center px-3 py-2.5 font-bold w-12"></th>
                <th className="text-right px-3 py-2.5 font-bold">Recipe Rate</th>
                <th className="text-right px-3 py-2.5 font-bold">Δ</th>
                <th className="text-left px-3 py-2.5 font-bold">Used in</th>
                <th className="text-center px-3 py-2.5 font-bold">Recipes</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="px-4 py-10 text-center text-[#4B5563]">Scanning…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-[#4B5563]">
                  <Sparkle size={14} weight="fill" className="inline mr-1 text-[#D4AF37]" />
                  All ingredients are in sync with your saved recipes.
                </td></tr>
              )}
              {filtered.map((s) => {
                const isOn = selected.has(s.name);
                const deltaCls = s.delta > 0 ? "text-emerald-700"
                  : s.delta < 0 ? "text-amber-700" : "text-[#4B5563]";
                return (
                  <tr key={s.name}
                      className={`border-t border-[#E5E7EB] transition cursor-pointer ${
                        isOn ? "bg-[#FDF9EE]" : "hover:bg-[#FDF9EE]/40"}`}
                      onClick={() => toggle(s.name)}
                      data-testid={`sync-recipe-row-${s.name.replace(/\s+/g, "-").toLowerCase()}`}>
                    <td className="px-3 py-2.5 text-center">
                      <input type="checkbox" checked={isOn} onChange={() => toggle(s.name)}
                             onClick={(e) => e.stopPropagation()}
                             className="h-4 w-4 accent-[#0A3B24]" />
                    </td>
                    <td className="px-4 py-2.5 font-display text-[#0A3B24]">{s.name}</td>
                    <td className="px-3 py-2.5 text-right font-mono-num text-[#4B5563]">
                      {s.current_master_rate != null ? `₹${s.current_master_rate.toFixed(2)}` :
                       <span className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">New</span>}
                    </td>
                    <td className="px-3 py-2.5 text-center text-[#D4AF37]"><ArrowRight size={14} weight="bold" /></td>
                    <td className="px-3 py-2.5 text-right font-mono-num font-bold text-[#0A3B24]">
                      ₹{Number(s.suggested_rate || 0).toFixed(2)}
                    </td>
                    <td className={`px-3 py-2.5 text-right font-mono-num text-xs ${deltaCls}`}>
                      {s.delta > 0 ? "+" : ""}₹{Number(s.delta).toFixed(2)}
                    </td>
                    <td className="px-3 py-2.5 text-[#4B5563] text-xs truncate max-w-[200px]">{s.last_used_in}</td>
                    <td className="px-3 py-2.5 text-center text-[#4B5563]">{s.recipe_count}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Tab 2 — Sync from Online (GPT-4o-mini market intel)
 * ----------------------------------------------------------------------- */
export function OnlineSyncPanel({ onApplied, masterItems }) {
  const [suggestions, setSuggestions] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [applying, setApplying] = useState(false);
  const [pickerNames, setPickerNames] = useState("");

  const fetchAll = async () => {
    setFetching(true); setSelected(new Set());
    try {
      const { data } = await api.post("/ingredients/master/sync-online", { all: true });
      setSuggestions(data.suggestions || []);
      toast.success(`Fetched ${data.returned} suggestions`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Online sync failed");
    } finally { setFetching(false); }
  };

  const fetchSubset = async () => {
    const names = pickerNames.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    if (!names.length) { toast.info("Enter at least one ingredient name"); return; }
    setFetching(true); setSelected(new Set());
    try {
      const { data } = await api.post("/ingredients/master/sync-online", { names });
      setSuggestions(data.suggestions || []);
      toast.success(`Fetched ${data.returned} suggestions`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Online sync failed");
    } finally { setFetching(false); }
  };

  const toggle = (name) => setSelected((s) => {
    const n = new Set(s); if (n.has(name)) n.delete(name); else n.add(name); return n;
  });
  const selectAll = () => setSelected(new Set(suggestions.filter((s) => s.suggested_rate != null).map((s) => s.name)));
  const clearAll = () => setSelected(new Set());

  const applyBatch = async () => {
    const items = suggestions
      .filter((s) => selected.has(s.name) && s.suggested_rate != null)
      .map((s) => ({ name: s.name, rate_per_kg: s.suggested_rate }));
    if (!items.length) { toast.info("Nothing selected"); return; }
    setApplying(true);
    try {
      const res = await api.post("/ingredients/master/apply-batch", { items });
      toast.success(`Applied ${res.data.applied.length} updates`);
      onApplied?.();
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch apply failed");
    } finally { setApplying(false); }
  };

  return (
    <div className="space-y-5" data-testid="sync-online-panel">
      <div className="kostra-card p-5">
        <h2 className="kostra-section-title text-lg flex items-center gap-2 mb-1">
          <Globe size={16} weight="duotone" className="text-[#D4AF37]" />
          Sync from Online Market Prices
        </h2>
        <p className="text-xs text-[#4B5563] mb-4 max-w-2xl">
          Uses GPT-4o-mini to estimate typical 2026 metro-India rates on Blinkit,
          Zepto, BigBasket, JioMart and DMart. Sanity-check &amp; selective bulk-apply —
          you stay in control.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
          <div className="md:col-span-8">
            <Label className="kostra-data-label mb-1.5 block">Selected ingredients (comma- or newline-separated)</Label>
            <textarea value={pickerNames}
                      onChange={(e) => setPickerNames(e.target.value)}
                      rows={2}
                      placeholder="e.g. Paneer, Chicken (Boneless), Garam Masala"
                      className="w-full rounded-md border-2 border-[#E5E7EB] focus:border-[#0A3B24] outline-none p-2.5 text-sm font-mono-num"
                      data-testid="sync-online-names" />
          </div>
          <div className="md:col-span-4 flex flex-col gap-2 pt-6">
            <Button onClick={fetchSubset} disabled={fetching || !pickerNames.trim()}
                    className="h-11 bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold"
                    data-testid="sync-online-fetch-selected">
              <ArrowsClockwise size={14} weight="bold" className="mr-1.5" />
              {fetching ? "Fetching…" : "Fetch for selected"}
            </Button>
            <Button onClick={fetchAll} disabled={fetching}
                    variant="outline"
                    className="h-11 border-[#D4AF37] text-[#A87C0E] hover:bg-[#FDF9EE] font-semibold"
                    data-testid="sync-online-fetch-all">
              <Globe size={14} weight="bold" className="mr-1.5" />
              {fetching ? "Fetching all…" : `Refresh all (${masterItems?.length || 0})`}
            </Button>
          </div>
        </div>
        {fetching && (
          <div className="mt-3 text-xs text-[#0A3B24] inline-flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-[#D4AF37] animate-ping" />
            <span className="font-semibold">Querying GPT-4o-mini for current market rates…</span>
            <span className="text-[#4B5563]">(~3s per 30 items)</span>
          </div>
        )}
      </div>

      {suggestions.length > 0 && (
        <div className="kostra-card p-0 overflow-hidden">
          <div className="px-5 py-3 flex items-center justify-between gap-3 border-b border-[#E5E7EB] flex-wrap">
            <div className="text-xs text-[#4B5563]">
              {suggestions.length} suggestion{suggestions.length === 1 ? "" : "s"} ·
              <span className="text-[#0A3B24] font-semibold ml-1">{selected.size} selected</span>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={selectAll}
                      className="text-xs font-semibold text-[#0A3B24] hover:underline">Select all</button>
              <span className="text-[#E5E7EB] text-xs">·</span>
              <button type="button" onClick={clearAll}
                      className="text-xs font-semibold text-[#4B5563] hover:underline">Clear</button>
              <Button onClick={applyBatch} disabled={!selected.size || applying}
                      className="h-9 bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold disabled:opacity-50"
                      data-testid="sync-online-apply">
                <Check size={13} weight="bold" className="mr-1.5" />
                {applying ? "Applying…" : `Apply ${selected.size || ""}`}
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sync-online-table">
              <thead className="bg-[#F9FAFB] text-[10px] uppercase tracking-wider text-[#4B5563]">
                <tr>
                  <th className="text-center px-3 py-2.5 font-bold w-10"></th>
                  <th className="text-left px-4 py-2.5 font-bold">Ingredient</th>
                  <th className="text-right px-3 py-2.5 font-bold">Master ₹/kg</th>
                  <th className="text-right px-3 py-2.5 font-bold">AI Suggested</th>
                  <th className="text-right px-3 py-2.5 font-bold">Δ</th>
                  <th className="text-center px-3 py-2.5 font-bold">Confidence</th>
                  <th className="text-left px-3 py-2.5 font-bold">Source Note</th>
                </tr>
              </thead>
              <tbody>
                {suggestions.map((s) => {
                  const isOn = selected.has(s.name);
                  const noRate = s.suggested_rate == null;
                  const deltaCls = (s.delta || 0) > 0 ? "text-emerald-700"
                    : (s.delta || 0) < 0 ? "text-amber-700" : "text-[#4B5563]";
                  const conf = s.confidence || "low";
                  const confCls = conf === "high" ? "bg-emerald-50 border-emerald-500 text-emerald-700"
                    : conf === "medium" ? "bg-[#FDF9EE] border-[#D4AF37] text-[#A87C0E]"
                    : "bg-[#F9FAFB] border-[#E5E7EB] text-[#4B5563]";
                  return (
                    <tr key={s.name}
                        className={`border-t border-[#E5E7EB] transition ${
                          noRate ? "opacity-60" :
                          isOn ? "bg-[#FDF9EE] cursor-pointer" : "hover:bg-[#FDF9EE]/40 cursor-pointer"}`}
                        onClick={() => !noRate && toggle(s.name)}
                        data-testid={`sync-online-row-${s.name.replace(/\s+/g, "-").toLowerCase()}`}>
                      <td className="px-3 py-2.5 text-center">
                        <input type="checkbox" checked={isOn} disabled={noRate}
                               onChange={() => !noRate && toggle(s.name)}
                               onClick={(e) => e.stopPropagation()}
                               className="h-4 w-4 accent-[#0A3B24]" />
                      </td>
                      <td className="px-4 py-2.5 font-display text-[#0A3B24]">{s.name}</td>
                      <td className="px-3 py-2.5 text-right font-mono-num text-[#4B5563]">
                        {s.current_master_rate != null ? `₹${s.current_master_rate.toFixed(2)}` :
                         <span className="text-[10px] uppercase text-amber-700 font-bold">New</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono-num font-bold text-[#0A3B24]">
                        {noRate ? "—" : `₹${Number(s.suggested_rate).toFixed(2)}`}
                      </td>
                      <td className={`px-3 py-2.5 text-right font-mono-num text-xs ${deltaCls}`}>
                        {noRate ? "—" : (s.delta > 0 ? "+" : "") + `₹${Number(s.delta).toFixed(2)}`}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border-2 font-bold ${confCls}`}>
                          {conf}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-[#4B5563] text-xs truncate max-w-[300px]"
                          title={s.source_note}>{s.source_note}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!suggestions.length && !fetching && (
        <div className="kostra-card p-8 text-center text-[#4B5563] text-sm">
          <Globe size={28} weight="duotone" className="inline mb-2 text-[#D4AF37]" />
          <div>Click <strong className="text-[#0A3B24]">Refresh all</strong> or paste names above to get current market estimates.</div>
        </div>
      )}
    </div>
  );
}
